import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Copy, Trash2, Check, Crown, UserPlus, Mail } from "lucide-react";

export default function MembersTab({ isOwner }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [role, setRole] = useState("analyst");
  const [copiedId, setCopiedId] = useState(null);
  const [emailFor, setEmailFor] = useState(null); // invite id
  const [emailAddr, setEmailAddr] = useState("");
  const [sending, setSending] = useState(false);
  const [fallbackLink, setFallbackLink] = useState(null); // { inviteId, url } when email not configured

  const load = () => {
    api.get("/workspace/members").then(r => setMembers(r.data));
    api.get("/workspace/invites").then(r => setInvites(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const createInvite = async () => {
    try {
      await api.post("/workspace/invites", { role });
      toast.success("Invite link created.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    await api.delete(`/workspace/invites/${id}`);
    toast.success("Revoked.");
    load();
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/workspace/members/${id}`);
    toast.success("Removed.");
    load();
  };

  const copyLink = async (code, id) => {
    const url = `${window.location.origin}/join/${code}`;
    const ok = await safeCopy(url);
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.success("Invite link copied.");
    } else {
      setFallbackLink({ inviteId: id, url });
      toast.info("Clipboard blocked — copy the link below.");
    }
  };

  const safeCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) { /* clipboard blocked (iframe / permissions-policy) */ }
    return false;
  };

  const sendEmail = async (inviteId) => {
    if (!emailAddr || !emailAddr.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/workspace/invites/${inviteId}/email`, { email: emailAddr });
      const link = data.link || "";
      if (data.delivered) {
        toast.success(`Invite emailed to ${emailAddr}.`);
        setFallbackLink(null);
        setEmailFor(null);
        setEmailAddr("");
      } else {
        const copied = await safeCopy(link);
        if (data.reason === "no_api_key") {
          toast.info(copied ? "Email not configured — link copied instead." : "Email not configured — copy the link below.", { duration: 5000 });
        } else {
          toast.error(`Could not send: ${data.reason || "unknown"}.${copied ? " Link copied." : " Copy the link below."}`);
        }
        setFallbackLink({ inviteId, url: link });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="members-tab">
      <Panel title={`// MEMBERS (${members.length})`}>
        <div className="divide-y divide-zinc-900">
          {members.map(m => (
            <div key={m.id} className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-heading font-bold text-sm">{m.name}</span>
                  {m.workspace_role === "owner" && <Crown className="w-3 h-3 text-[#FFB000]" />}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">{m.email}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{m.role}</span>
                {isOwner && m.workspace_role !== "owner" && (
                  <button
                    onClick={() => removeMember(m.id)}
                    data-testid={`remove-${m.id}`}
                    className="text-zinc-600 hover:text-[#FF3B30]"
                    title="Remove"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="// INVITES" meta={isOwner ? "" : "read-only"}>
        {isOwner && (
          <div className="p-4 border-b border-zinc-900 flex items-end gap-2">
            <div className="flex-1">
              <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">ROLE</label>
              <select
                data-testid="invite-role"
                value={role} onChange={e => setRole(e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 px-2 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
              >
                <option value="analyst">Analyst</option>
                <option value="executive">Executive</option>
              </select>
            </div>
            <button
              onClick={createInvite}
              data-testid="create-invite-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] flex items-center gap-1.5"
            ><UserPlus className="w-3.5 h-3.5" /> CREATE INVITE</button>
          </div>
        )}
        <div className="divide-y divide-zinc-900 max-h-[500px] overflow-auto">
          {invites.length === 0 && <div className="p-6 text-center font-mono text-[11px] text-zinc-600">No invites yet.</div>}
          {invites.map(i => (
            <div key={i.id} className="p-3 space-y-2" data-testid="invite-row">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-zinc-400 truncate">/join/{i.code}</div>
                  <div className="font-mono text-[9px] text-zinc-600 mt-1">
                    {i.role} · {i.used ? <span className="text-[#00E676]">USED</span> : <span className="text-[#FFB000]">PENDING</span>}
                    {i.last_emailed_to && <span className="text-zinc-500"> · emailed {i.last_emailed_to}</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  {!i.used && (
                    <>
                      <button
                        onClick={() => { setEmailFor(emailFor === i.id ? null : i.id); setEmailAddr(""); }}
                        data-testid={`email-invite-${i.id}`}
                        className="p-1.5 border border-zinc-800 hover:border-[#FFB000]/50 hover:text-[#FFB000]"
                        title="Email invite"
                      ><Mail className="w-3 h-3" /></button>
                      <button
                        onClick={() => copyLink(i.code, i.id)}
                        data-testid={`copy-invite-${i.id}`}
                        className="p-1.5 border border-zinc-800 hover:border-[#FFB000]/50 hover:text-[#FFB000]"
                      >{copiedId === i.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}</button>
                    </>
                  )}
                  {isOwner && !i.used && (
                    <button
                      onClick={() => revoke(i.id)}
                      className="p-1.5 border border-zinc-800 hover:border-[#FF3B30]/50 hover:text-[#FF3B30]"
                    ><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              </div>
              {emailFor === i.id && (
                <div className="flex gap-2" data-testid={`email-form-${i.id}`}>
                  <input
                    type="email"
                    value={emailAddr}
                    onChange={e => setEmailAddr(e.target.value)}
                    placeholder="colleague@company.com"
                    data-testid={`email-input-${i.id}`}
                    className="flex-1 bg-black border border-zinc-800 px-2 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-[#FFB000]"
                  />
                  <button
                    onClick={() => sendEmail(i.id)}
                    disabled={sending}
                    data-testid={`send-email-${i.id}`}
                    className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-[#E59E00] disabled:opacity-50"
                  >{sending ? "SENDING…" : "SEND"}</button>
                </div>
              )}
              {fallbackLink?.inviteId === i.id && (
                <div className="mt-1 p-2 bg-zinc-950 border border-zinc-800" data-testid={`fallback-link-${i.id}`}>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">COPY THIS LINK</div>
                  <input
                    readOnly
                    onFocus={e => e.target.select()}
                    value={fallbackLink.url}
                    className="w-full bg-black border border-zinc-800 px-2 py-1 font-mono text-[10px] text-[#FFB000] focus:outline-none"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
